import { Transition } from "@headlessui/react";
import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import CommonSwitch from "../../common/components/switch";
import Error from "../../common/components/error";
import Loading from "../../common/components/loading";
import { NotificationContext } from "../../common/context/notification";
import { classNames } from "../../common/utils/classnames";
import { useTitle } from "../../common/utils/title";
import {
  useBobQuickMessageConfigsQuery,
  useEditBobQuickMessageConfigMutation,
} from "../../graphql/generated";

// The admin overlay only exposes presentation + gating; the config's `sort_order` is
// owned by the list's drag-to-reorder, so `edit` preserves whatever order the row has.
const BobQuickMessageEdit: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { notifier } = React.useContext(NotificationContext);

  // There's no single-config query; the list query is cheap (single-digit rows) and
  // cache-and-network keeps it warm, so read the one being edited from it.
  const [{ data, error: queryError }] = useBobQuickMessageConfigsQuery({
    requestPolicy: "cache-and-network",
  });
  const config = data?.bobQuickMessageConfigs.find(
    (candidate) => candidate.id === params.bobQuickMessageId
  );
  // Query resolved without error but the id isn't in the list (deep-link, deleted, typo).
  const notFound = !!data && !config;

  useTitle("Edit", config?.name ?? params.bobQuickMessageId, "Bob Quick Messages");

  const [enabled, setEnabled] = React.useState(true);
  const [title, setTitle] = React.useState("");
  const [subtitle, setSubtitle] = React.useState("");
  const [icon, setIcon] = React.useState("");
  const [featureFlagId, setFeatureFlagId] = React.useState("");

  React.useEffect(() => {
    if (config) {
      setEnabled(config.enabled);
      setTitle(config.title ?? "");
      setSubtitle(config.subtitle ?? "");
      setIcon(config.icon ?? "");
      setFeatureFlagId(config.featureFlagId ?? "");
    }
  }, [config]);

  const [{ error, data: mutationData, fetching }, mutation] =
    useEditBobQuickMessageConfigMutation();

  const trimmedOrNull = (value: string): string | undefined => {
    const trimmed = value.trim();
    return trimmed === "" ? undefined : trimmed;
  };

  const submitForm = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!config) return;

      mutation({
        id: config.id,
        details: {
          sortOrder: config.sortOrder,
          enabled,
          featureFlagId: trimmedOrNull(featureFlagId),
          title: trimmedOrNull(title),
          subtitle: trimmedOrNull(subtitle),
          icon: trimmedOrNull(icon),
        },
      }).then(notifier.notifyGraphql());
    },
    [config, enabled, featureFlagId, title, subtitle, icon, mutation, notifier]
  );

  React.useEffect(() => {
    if (!error && mutationData && !fetching) {
      navigate("/bob_quick_messages");
    } else if (error) {
      notifier.error(error);
    }
  }, [mutationData, error, fetching, navigate, notifier]);

  const inputClass =
    "max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md";
  const labelClass =
    "block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2";
  const rowClass =
    "sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5";

  return (
    <>
      <Loading show={!config && !queryError && !notFound} />
      <Error error={queryError} />
      {notFound && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <p className="text-sm text-gray-700">
            No quick message with id{" "}
            <span className="font-medium">{params.bobQuickMessageId}</span>.{" "}
            <Link
              to="/bob_quick_messages"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              Back to Bob Quick Messages
            </Link>
          </p>
        </div>
      )}
      <Transition
        show={!!config}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Edit Quick Message
          </h2>
        </div>

        <div className="bg-white shadow overflow-hidden sm:mx-4 sm:rounded-md">
          <div className="p-4">
            <form
              className="space-y-8 divide-y divide-gray-200"
              onSubmit={submitForm}
            >
              <div className="mt-6 sm:mt-5 space-y-6 sm:space-y-5">
                <div className={rowClass}>
                  <span className={labelClass}>ID</span>
                  <div className="mt-1 sm:mt-0 sm:col-span-2 sm:pt-2 text-sm text-gray-500">
                    {config?.id}
                  </div>
                </div>

                <div className={rowClass}>
                  <span className={labelClass}>Enabled</span>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <CommonSwitch
                      label={enabled ? "Shown" : "Hidden"}
                      enabled={enabled}
                      toggle={() => setEnabled(!enabled)}
                    />
                  </div>
                </div>

                <div className={rowClass}>
                  <label htmlFor="title" className={labelClass}>
                    Title override
                  </label>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <input
                      type="text"
                      id="title"
                      value={title}
                      placeholder={config?.defaultName}
                      onChange={(e) => setTitle(e.target.value)}
                      className={inputClass}
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      Leave blank to use the default: {config?.defaultName}
                    </p>
                  </div>
                </div>

                <div className={rowClass}>
                  <label htmlFor="subtitle" className={labelClass}>
                    Subtitle override
                  </label>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <input
                      type="text"
                      id="subtitle"
                      value={subtitle}
                      placeholder={config?.defaultDescription}
                      onChange={(e) => setSubtitle(e.target.value)}
                      className={inputClass}
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      Leave blank to use the default: {config?.defaultDescription}
                    </p>
                  </div>
                </div>

                <div className={rowClass}>
                  <label htmlFor="icon" className={labelClass}>
                    Icon
                  </label>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <input
                      type="text"
                      id="icon"
                      value={icon}
                      onChange={(e) => setIcon(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className={rowClass}>
                  <label htmlFor="featureFlagId" className={labelClass}>
                    Feature flag
                  </label>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <input
                      type="text"
                      id="featureFlagId"
                      value={featureFlagId}
                      onChange={(e) => setFeatureFlagId(e.target.value)}
                      className={inputClass}
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      A feature flag slug. When set, the quick message is only
                      shown to accounts the flag is enabled for. Must be an
                      existing flag.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-5">
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </button>
                  <button
                    disabled={fetching}
                    type="submit"
                    className={classNames(
                      "text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500",
                      "ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md"
                    )}
                  >
                    Save
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </>
  );
};

export default BobQuickMessageEdit;
